import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, Loader2, ClipboardList } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import Pagination from '../../components/ui/pagination';

const ACTION_COLORS = {
  CREATE: 'bg-green-100 text-green-700',
  UPDATE: 'bg-blue-100 text-blue-700',
  DELETE: 'bg-red-100 text-red-700',
};

const RESOURCE_LABELS = {
  customer: 'Customer',
  vendor: 'Vendor',
  docket: 'Docket',
  trip: 'Trip',
  booking: 'Booking',
};

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

const ActivityLog = () => {
  const { toast } = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [resourceFilter, setResourceFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [activeUsers, setActiveUsers] = useState([]);

  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json',
  }), []);

  // Detect role
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  useEffect(() => {
    try {
      const token = localStorage.getItem('auth_token');
      const payload = JSON.parse(atob(token.split('.')[1]));
      setIsSuperAdmin(payload.role === 'super_admin');
    } catch {}
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${backendUrl}/api/activity-logs/active-users`, { headers: getAuthHeaders() });
      if (res.ok) setActiveUsers(await res.json());
    } catch {}
  }, [backendUrl, getAuthHeaders]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (resourceFilter !== 'all') params.append('resource_type', resourceFilter);
      if (actionFilter !== 'all') params.append('action_type', actionFilter);
      if (userFilter !== 'all') params.append('user_id', userFilter);
      if (search) params.append('search', search);
      params.append('skip', String((page - 1) * pageSize));
      params.append('limit', String(pageSize));
      const res = await fetch(`${backendUrl}/api/activity-logs?${params}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      setLogs(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toast({ title: 'Error', description: 'Failed to load activity log', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [backendUrl, getAuthHeaders, resourceFilter, actionFilter, userFilter, search, page, pageSize, toast]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { setPage(1); }, [resourceFilter, actionFilter, userFilter]);

  return (
    <div className="space-y-6" data-testid="activity-log-page">
      <div className="flex items-center gap-3">
        <ClipboardList className="w-7 h-7 text-text-dark" />
        <div>
          <h2 className="text-2xl font-bold text-text-dark font-manrope">Activity Log</h2>
          <p className="text-sm text-text-secondary font-ibm mt-0.5">Audit trail — who created and updated records</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
            <Input
              data-testid="activity-log-search"
              placeholder="Search by name or user..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="pl-10 font-ibm"
            />
          </div>

          <Select value={resourceFilter} onValueChange={setResourceFilter}>
            <SelectTrigger data-testid="resource-filter" className="w-44 font-ibm">
              <SelectValue placeholder="All Resources" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Resources</SelectItem>
              <SelectItem value="docket">Docket</SelectItem>
              <SelectItem value="customer">Customer</SelectItem>
              <SelectItem value="trip">Trip</SelectItem>
              <SelectItem value="vendor">Vendor</SelectItem>
              <SelectItem value="booking">Booking</SelectItem>
            </SelectContent>
          </Select>

          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger data-testid="action-filter" className="w-36 font-ibm">
              <SelectValue placeholder="All Actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              <SelectItem value="CREATE">Create</SelectItem>
              <SelectItem value="UPDATE">Update</SelectItem>
              <SelectItem value="DELETE">Delete</SelectItem>
            </SelectContent>
          </Select>

          {activeUsers.length > 0 && (
            <Select value={userFilter} onValueChange={setUserFilter}>
              <SelectTrigger data-testid="user-filter" className="w-48 font-ibm">
                <SelectValue placeholder="All Users" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                {activeUsers.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <span className="text-sm text-text-secondary font-ibm ml-auto">
            {total} event{total !== 1 ? 's' : ''}
          </span>
        </div>
      </Card>

      {/* Table */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-7 h-7 animate-spin text-accent-blue" />
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <ClipboardList className="w-12 h-12 text-text-secondary/30" />
            <p className="text-text-secondary font-ibm">No activity logs found</p>
            <p className="text-xs text-text-secondary font-ibm">Activity is recorded when records are created or updated going forward.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="activity-log-table">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Date & Time', 'Action', 'Resource', 'Record Name', 'User', 'Role', ...(isSuperAdmin ? ['Details'] : [])].map(h => (
                    <th key={h} className="px-4 py-3.5 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50 transition-colors" data-testid={`log-row-${log.id}`}>
                    <td className="px-4 py-3 text-sm text-text-secondary font-ibm whitespace-nowrap">
                      {formatDate(log.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={`border-0 text-xs font-ibm font-semibold ${ACTION_COLORS[log.action_type] || 'bg-gray-100 text-gray-600'}`}>
                        {log.action_type}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-dark font-ibm capitalize">
                      {RESOURCE_LABELS[log.resource_type] || log.resource_type}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-text-dark font-ibm">
                      {log.resource_name || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-dark font-ibm">
                      {log.user_name || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary font-ibm capitalize">
                      {log.user_role ? log.user_role.replace('_', ' ') : '—'}
                    </td>
                    {isSuperAdmin && (
                      <td className="px-4 py-3 text-xs text-text-secondary font-ibm">
                        {log.details || '—'}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && logs.length > 0 && (
          <Pagination
            total={total}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={() => {}}
          />
        )}
      </Card>
    </div>
  );
};

export default ActivityLog;
